import React from 'react';
import { ArrowLeftRight } from 'lucide-react';
import {
  TRANSPORT_PREFERENCE_HINT,
  TRANSPORT_PREFERENCE_KEYS,
  TRANSPORT_PREFERENCE_LABEL,
} from '../../lib/rcu/catalog/link';
import { useRcuLinkStore } from '../../stores/rcuLinkStore';

export interface TransportPickerProps {
  /** `compact` fits the console header; `full` carries the hints in the sheet. */
  readonly variant: 'compact' | 'full';
}

/**
 * Which channel commands leave on, for the whole tablet.
 *
 * It is here rather than buried in a panel because it decides what every other
 * screen does: the guest room controls obey it too, so a technician can put the
 * installation on HTTP to prove a light works without a broker, or on MQTT to
 * insist every command is answered.
 */
export const TransportPicker: React.FC<TransportPickerProps> = ({ variant }) => {
  const transport = useRcuLinkStore((state) => state.settings.transport);
  const setTransport = useRcuLinkStore((state) => state.setTransport);

  if (variant === 'compact') {
    return (
      <div
        className="flex items-center gap-1 rounded-full border border-white/20 p-0.5"
        role="radiogroup"
        aria-label="Command transport"
      >
        <ArrowLeftRight className="ml-2 h-4 w-4 shrink-0 text-white/50" aria-hidden />
        {TRANSPORT_PREFERENCE_KEYS.map((key) => {
          const isActive = key === transport;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={isActive}
              title={TRANSPORT_PREFERENCE_HINT[key]}
              onClick={() => setTransport(key)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                isActive ? 'bg-[#D4A054] text-[#1A1A1A]' : 'text-white/60 hover:text-white'
              }`}
            >
              {TRANSPORT_PREFERENCE_LABEL[key]}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-[#333333]">Command transport</span>
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Command transport">
        {TRANSPORT_PREFERENCE_KEYS.map((key) => {
          const isActive = key === transport;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => setTransport(key)}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                isActive
                  ? 'border-[#D4A054] bg-[#FDF6E9]'
                  : 'border-[#E0E0E0] bg-white hover:border-[#D4A054]/60'
              }`}
            >
              <span className="block text-sm font-medium text-[#333333]">
                {TRANSPORT_PREFERENCE_LABEL[key]}
              </span>
              <span className="mt-0.5 block text-xs text-[#666666]">
                {TRANSPORT_PREFERENCE_HINT[key]}
              </span>
            </button>
          );
        })}
      </div>
      <span className="mt-1.5 block text-xs text-[#666666]">
        The room controls on this tablet follow the same choice, so what you see here is what a
        guest gets.
      </span>
    </div>
  );
};

export default TransportPicker;
